import { ArrowLeft, Company, Share } from "@ignite-analytics/icons";
import EmailIcon from "@mui/icons-material/Email";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { Questionnaire } from "../../../globals/types";
import useToken from "../../../hooks/useToken";
import { LocalizedContent } from "../../../lib/i18n/contentTranslations";
import { LanguageSwitcher } from "../../LanguageSwitcher";

type Props = {
    questionnaire?: Questionnaire;
    supplierName?: string;
    url?: string;
    /**
     * Snapshot of the BU + spend category the initiator selected on the
     * spawning performance evaluation. Pass undefined / null when the
     * assessment is not a scoped PE — the header hides the line.
     */
    evaluationScope?: { businessUnitName?: string | null; spendCategoryName?: string | null } | null;
    hideLanguageSwitcher?: boolean;
};

const QuestionnaireHeader: React.FC<Props> = ({
    questionnaire,
    supplierName,
    url,
    evaluationScope,
    hideLanguageSwitcher,
}) => {
    const numberOfQuestions = useMemo(
        () => questionnaire?.sections.reduce((prevVal, currVal) => prevVal + currVal.questions.length, 0),
        [questionnaire]
    );

    const { decoded } = useToken();
    const isFeedbackFlow = !!decoded.receiverEmail;
    const tenantName = decoded.customerName ?? "";
    const userEmail = decoded.receiverEmail;

    const businessUnitName = evaluationScope?.businessUnitName ?? null;
    const spendCategoryName = evaluationScope?.spendCategoryName ?? null;
    const showScope = !!(businessUnitName || spendCategoryName);

    const rawName = questionnaire?.name || "";
    const suffix = supplierName ? ` - ${supplierName}` : "";
    const questionnaireName = suffix && rawName.endsWith(suffix) ? rawName.slice(0, -suffix.length) : rawName;

    return (
        <>
            <Box
                sx={{
                    paddingX: { xs: 2, sm: 3 },
                    paddingTop: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                {isFeedbackFlow ? (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        paddingY={{ xs: 1, sm: 2 }}
                        paddingX={{ xs: 0.5, sm: 0 }}
                    >
                        {url && (
                            <IconButton component={Link} to={url}>
                                <ArrowLeft />
                            </IconButton>
                        )}
                        <Stack alignItems="center" flex={1} minWidth={0}>
                            <Typography variant="textSm">
                                <FormattedMessage
                                    defaultMessage="Evaluating {supplierName}"
                                    values={{ supplierName }}
                                />
                            </Typography>
                            <Typography variant="textSm" fontWeight={500} color="textTextPrimary">
                                <LocalizedContent
                                    translationKey={questionnaire?.nameTranslationKey}
                                    fallback={questionnaire?.name || ""}
                                />
                            </Typography>
                        </Stack>
                        {!hideLanguageSwitcher && <LanguageSwitcher />}
                    </Stack>
                ) : (
                    <Stack paddingY={{ xs: 0, sm: 1 }} paddingX={{ xs: 0.5, sm: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                {url && (
                                    <IconButton component={Link} to={url}>
                                        <ArrowLeft />
                                    </IconButton>
                                )}
                                <Stack gap={0.25}>
                                    <Typography variant="textLg" fontWeight={600}>
                                        <LocalizedContent
                                            translationKey={questionnaire?.nameTranslationKey}
                                            fallback={questionnaireName}
                                        />
                                    </Typography>
                                    {supplierName && (
                                        <Typography variant="textSm" color="textTextHelper">
                                            {supplierName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                            {!hideLanguageSwitcher && <LanguageSwitcher />}
                        </Stack>
                    </Stack>
                )}
            </Box>
            {isFeedbackFlow && (
                <Stack gap={1} paddingTop={5} paddingBottom={2} paddingX={{ xs: 2, sm: 3 }}>
                    <Stack direction="column" gap={0.5}>
                        <Typography variant="textSm" color="textTextPrimary" component="p">
                            <FormattedMessage
                                defaultMessage="Your input helps {tenantName} evaluate supplier performance and identify improvement areas."
                                values={{ tenantName }}
                            />
                        </Typography>
                        <Typography variant="textSm" color="textTextPrimary" component="p">
                            <FormattedMessage defaultMessage="All questions are optional, please answer where you have relevant experience." />
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1}>
                        <Typography variant="textSm" color="textTextHelper">
                            <FormattedMessage
                                defaultMessage="{count, plural, one {1 question} other {{count} questions}}"
                                values={{ count: numberOfQuestions }}
                            />
                        </Typography>
                        {userEmail && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <EmailIcon fontSize="small" />
                                <Typography>{userEmail}</Typography>
                            </Stack>
                        )}
                    </Stack>
                    {showScope && (
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" pt={1}>
                            <Typography variant="textSm" color="textTextHelper">
                                <FormattedMessage defaultMessage="Scope:" />
                            </Typography>
                            {spendCategoryName && (
                                <Chip
                                    size="small"
                                    color="category"
                                    variant="status"
                                    icon={<Share />}
                                    label={spendCategoryName}
                                />
                            )}
                            {businessUnitName && (
                                <Chip
                                    size="small"
                                    color="businessUnit"
                                    variant="status"
                                    icon={<Company />}
                                    label={businessUnitName}
                                />
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </>
    );
};

export default QuestionnaireHeader;
